import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { EditableField, EditableFieldSection } from '../models/ui/editable-field-section.model';
import { Collection } from '../models/ui/collection.model';
import { EditableFieldNode } from '../models/ui/editable-field-node.model';
import { CaseFieldSdk } from '../sdks/case-field.sdk';
import { AccountSdk } from '../sdks/account.sdk';
import { Account } from '../models/ui/account.model';
import { AccountResource } from '../models/resource/account.resource';
import { AccountConfigSdk } from '../sdks/account-config.sdk';
import { AccountConfig } from '../models/account-config.model';
import { ExportSdk } from '../sdks/export.sdk';
import { UserConfigsService } from '../services/user-configs.service';
import { Role } from '../models/role.model';

@Injectable({
	providedIn: 'root'
})
export class AccountManager {
	public editableFieldCollection$: Observable<Collection<EditableFieldSection>>;
	public accountCollection$: Observable<Account[]>;

	private editableFieldCollectionSource = new BehaviorSubject<Collection<EditableFieldSection>>(null);
	private accountCollectionSource: BehaviorSubject<Account[]> = new BehaviorSubject<Account[]>([]);

	private role = Role;

	constructor(
		private caseFieldSdk: CaseFieldSdk,
		private accountSdk: AccountSdk,
		private accountConfigSdk: AccountConfigSdk,
		private exportSdk: ExportSdk,
		private userConfigsService: UserConfigsService
	) {
		this.editableFieldCollection$ = this.editableFieldCollectionSource.asObservable();
		this.accountCollection$ = this.accountCollectionSource.asObservable();
	}

	//#region EditableField

	public refreshEditableFields(): void {
		this.caseFieldSdk.get().subscribe((response) => {
			this.editableFieldCollectionSource.next(response);
		});
	}

	public createEditableField(params?: CreateEditableFieldParams): Observable<EditableField> {
		return this.caseFieldSdk.post({ field: params?.field });
	}

	public saveEditableField(params?: SaveEditableFieldParams): Observable<EditableField> {
		return this.caseFieldSdk.put({ field: params?.field });
	}

	public removeEditableField(params?: RemoveEditableFieldParams): Observable<void> {
		return this.caseFieldSdk.delete({ field: params?.field });
	}

	//#endregion EditableField

	//#region Accounts

	public refreshAdminAccounts(searchTerm?: string): void {
		this.getAdminAccounts(searchTerm).subscribe(data => { this.accountCollectionSource.next(data) });
	}

	public getAdminAccounts(searchTerm?: string): Observable<Account[]> {
		return this.accountSdk.getAccounts(searchTerm);
	}

	public getManagedAccounts(isManageBilling: boolean, searchTerm?: string): Observable<Account[]> {
		if (isManageBilling && this.userConfigsService.inRoles(Role.Accounting, Role.Administrator))
			return this.accountSdk.getAccounts(searchTerm);
		else if (!isManageBilling && this.userConfigsService.inRoles(Role.Administrator))
			return this.accountSdk.getAccounts(searchTerm);
		else
			return this.accountSdk.getAccountsManaged(searchTerm);
	}

	public getAccountExpanded(accountId: string): Observable<Account> {
		return this.accountSdk.getAccount(accountId);
	}

	public createAccount(account: Account): Observable<string> {
		return this.accountSdk.postAccount(account);
	}

	public updateAccount(accountId: string, deltaAccount: Partial<AccountResource>): Observable<void> {
		return this.accountSdk.patchAccount(accountId, deltaAccount);
	}

	public deleteAccount(accountId: string): Observable<void> {
		return this.accountSdk.deleteAccount(accountId);
	}

	public exportAccounts(accountId: string): Observable<string> {
		return this.exportSdk.exportAccounts(accountId);
	}

	//#endregion Accounts

	//#region Account Config

	public getAccountConfigs(accountId: string): Observable<AccountConfig[]> {
		return this.accountConfigSdk.getAccountConfigs(accountId);
	}

	public updateAccountConfig(config: AccountConfig): Observable<void> {
		return this.accountConfigSdk.updateAccountConfig(config);
	}

	//#endregion Account Config

}


interface CreateEditableFieldParams {
	field: EditableFieldSection | EditableFieldNode;
}

interface SaveEditableFieldParams {
	field: EditableFieldSection | EditableFieldNode;
}

interface RemoveEditableFieldParams {
	field: EditableFieldSection | EditableFieldNode;
}
